import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Plus, Loader2, FileText, Trash2, Edit, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../lib/admin';
import { ChapterType } from '../lib/database.types';

interface Chapter {
  chapter_id: string;
  chapter_title: string;
  chapter_type: ChapterType;
  status: string;
}

interface Book {
  book_id: string;
  title: string;
  created_at: string;
  chapters: Chapter[];
}

export function Dashboard() {
  const { user } = useAuth();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchBooks() {
    try {
      const { data: booksData, error: booksError } = await supabase
        .from('books_metadata')
        .select(`
          book_id,
          title,
          created_at,
          chapters:books_chapters(
            chapter_id,
            chapter_title,
            chapter_type,
            status
          )
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (booksError) throw booksError;
      setBooks(booksData || []);
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDeleteBook(bookId: string) {
    if (!confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('books_metadata')
        .delete()
        .eq('book_id', bookId);

      if (error) throw error;
      setBooks(books.filter(book => book.book_id !== bookId));
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Error deleting book. Please try again.');
    }
  }

  async function handleSignOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error('Error signing out:', error);
      alert('Error signing out. Please try again.');
    }
  }

  function formatDate(dateString: string) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  }

  function getBookStructure(chapters: Chapter[]) {
    const structure = {
      introduction: chapters.filter(ch => ch.chapter_type === 'introduction').length,
      chapters: chapters.filter(ch => ch.chapter_type === 'chapter').length,
      conclusion: chapters.filter(ch => ch.chapter_type === 'conclusion').length,
      bonus: chapters.filter(ch => ['glossary', 'checklist', 'appendix', 'bonus', 'faq'].includes(ch.chapter_type)).length
    };

    const parts = [];
    if (structure.introduction) parts.push('Introduction');
    if (structure.chapters) parts.push(`${structure.chapters} ${structure.chapters === 1 ? 'Chapter' : 'Chapters'}`);
    if (structure.conclusion) parts.push('Conclusion');
    if (structure.bonus) parts.push(`${structure.bonus} ${structure.bonus === 1 ? 'Bonus Section' : 'Bonus Sections'}`);

    return parts.join(', ');
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <BookOpen className="h-8 w-8 text-indigo-600" />
              <h1 className="ml-2 text-xl font-bold text-gray-900">My Books</h1>
            </div>
            <div className="flex items-center space-x-4">
              {isAdmin(user) && (
                <Link
                  to="/admin"
                  className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 flex items-center"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Admin
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Books</h2>
            <Link
              to="/new-book"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Book
            </Link>
          </div>

          {books.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No books</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new book.</p>
              <div className="mt-6">
                <Link
                  to="/new-book"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Book
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {books.map((book) => (
                  <li key={book.book_id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-medium text-indigo-600 truncate">
                            {book.title}
                          </h3>
                          <div className="mt-2">
                            <div className="flex items-center text-sm text-gray-500">
                              <span>{getBookStructure(book.chapters)}</span>
                              <span className="mx-2">&middot;</span>
                              <span>
                                Created on {formatDate(book.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <Link
                            to={`/books/${book.book_id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <Edit className="h-5 w-5" />
                          </Link>
                          <button
                            onClick={() => handleDeleteBook(book.book_id)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}